class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.pageScrollPosition = null;
    this.restoreFrame = null;
    this.restoreFocusTimer = null;
    this.openRestoreTimer = null;
    this.stickyTrigger = null;
    this.stickyClickHandler = (event) => {
      const stickyTrigger = event.target.closest?.(".vzy-qb-sticky__add");
      if (stickyTrigger) {
        this.stickyTrigger = stickyTrigger;
        this.pageScrollPosition = {
          x: window.scrollX,
          y: window.scrollY,
        };
        this.setActiveElement(stickyTrigger);
      }
    };
    document.addEventListener("click", this.stickyClickHandler);

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this, false)
    );
    this.setHeaderCartIconAccessibility();

    const headerAccount = document.querySelector(".header__account");
    if (headerAccount) {
      headerAccount.addEventListener("click", () => {
        if (this.classList.contains("active")) {
          this.close();
        }
      });
    }

    const search = document.querySelector(".header__search");
    if (search) {
      search.addEventListener("click", () => {
        if (search.isOpen && this.classList.contains("active")) {
          this.close();
        }
      });
    }

    const localization = document.querySelectorAll(".header__localization");
    localization.forEach((localization) => {
      localization.addEventListener("click", () => {
        if (
          localization.querySelector("button[aria-expanded=true]") &&
          this.classList.contains("active")
        ) {
          this.close();
        }
      });
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      if (!this.classList.contains("active")) this.open(cartLink);
      else this.close();
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        if (!this.classList.contains("active")) this.open(cartLink);
        else this.close();
      }
    });
  }

  open(triggeredBy) {
    if (!this.classList.contains("active")) {
      if (triggeredBy || !this.pageScrollPosition) {
        this.pageScrollPosition = {
          x: window.scrollX,
          y: window.scrollY,
        };
      }
      if (triggeredBy) this.stickyTrigger = null;
    }

    const search = document.querySelector(".header__search");
    if (search) {
      search.close();
    }
    const annBar = document.querySelector(".section-announcement");
    if (annBar) annBar.classList.add("section-announcement-overlay");

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    } else if (!this.activeElement?.isConnected) {
      const fallbackTrigger = document.querySelector(".vzy-qb-sticky__add");
      if (fallbackTrigger) this.setActiveElement(fallbackTrigger);
    }
    if (!triggeredBy && !this.stickyTrigger) {
      this.stickyTrigger = document.querySelector(".vzy-qb-sticky__add");
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
      requestAnimationFrame(() =>
        this.querySelector(".drawer__close")?.focus({ preventScroll: true })
      );
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__close") ||
          this.querySelector(".drawer__inner");
        trapFocus(containerToTrapFocusOn, focusElement);
        this.restorePageScroll();
        window.clearTimeout(this.openRestoreTimer);
        this.openRestoreTimer = window.setTimeout(() => {
          this.restorePageScroll();
          this.openRestoreTimer = null;
        }, 450);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden-drawer");
  }

  close() {
    const annBar = document.querySelector(".section-announcement");
    if (annBar) annBar.classList.remove("section-announcement-overlay");

    const activeElementTarget = this.activeElement?.closest?.("cart-drawer")
      ? null
      : this.activeElement;
    const focusTarget =
      this.stickyTrigger ||
      activeElementTarget ||
      document.querySelector(".vzy-qb-sticky__add");
    const scrollPosition = this.pageScrollPosition;

    this.classList.remove("active");
    removeTrapFocus();
    document.body.classList.remove("overflow-hidden-drawer");

    if (this.restoreFrame) cancelAnimationFrame(this.restoreFrame);
    this.restoreFrame = requestAnimationFrame(() => {
      if (scrollPosition) window.scrollTo(scrollPosition.x, scrollPosition.y);

      this.restoreFrame = requestAnimationFrame(() => {
        if (scrollPosition) window.scrollTo(scrollPosition.x, scrollPosition.y);
        if (
          focusTarget?.isConnected &&
          !focusTarget.closest("[inert]") &&
          !focusTarget.classList.contains("card-focused")
        ) {
          focusTarget.focus({ preventScroll: true });
        }
        this.restoreFrame = null;
      });
    });

    window.clearTimeout(this.restoreFocusTimer);
    this.restoreFocusTimer = window.setTimeout(() => {
      if (scrollPosition) window.scrollTo(scrollPosition.x, scrollPosition.y);
      if (
        focusTarget?.isConnected &&
        !focusTarget.closest("[inert]") &&
        !focusTarget.classList.contains("card-focused")
      ) {
        focusTarget.focus({ preventScroll: true });
      }
      this.pageScrollPosition = null;
      this.stickyTrigger = null;
      this.restoreFocusTimer = null;
    }, 420);
  }

  restorePageScroll() {
    if (!this.pageScrollPosition) return;
    window.scrollTo(
      this.pageScrollPosition.x,
      this.pageScrollPosition.y
    );
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this, false)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
